import axios from 'axios';

// Dynamic API URL - runtime config with dev VM fallback
// On dev VMs, always use HTTP:5213 regardless of frontend protocol
export const API_BASE_URL = window.__config__?.API_BASE_URL
  || (window.location.hostname === 'localhost'
      ? 'http://localhost:5213'
      : `http://${window.location.hostname}:5213`);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Inject Bearer token for Entra auth mode
api.interceptors.request.use(async (config) => {
  const authMode = window.__config__?.AUTH_MODE || 'impersonation';
  if (authMode === 'entra') {
    try {
      const { msalInstance } = await import('../auth/authConfig');
      if (msalInstance) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: [`api://${window.__config__?.AZURE_CLIENT_ID}/access_as_user`],
              account: accounts[0],
            });
            config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
          } catch (silentErr) {
            console.warn('Silent token acquisition failed, redirecting:', silentErr);
            msalInstance.acquireTokenRedirect({
              scopes: [`api://${window.__config__?.AZURE_CLIENT_ID}/access_as_user`],
            });
          }
        }
      }
    } catch (err) {
      console.error('Failed to acquire auth token:', err);
    }
  }
  return config;
});

// Inject selected database into every API request
api.interceptors.request.use(config => {
  const selectedDb = localStorage.getItem('pmd-selected-database');
  if (selectedDb) {
    config.headers['X-Database-Id'] = selectedDb;
  }
  return config;
});

// View-based lookups (Parent_Unique, Panel_Unique, Positive_Terminal, etc.) derive
// their values from live project-DB queries. When a row is created/updated/deleted
// via dynamicApi, the set of possible dropdown values may have changed. Dispatch a
// window event so LookupContext refetches — backend already purges its own cache
// on the same events (see DynamicController.InvalidateLookupsCache).
api.interceptors.response.use(response => {
  const method = response.config.method?.toLowerCase();
  const url = response.config.url || '';
  if ((method === 'post' || method === 'put' || method === 'delete') && url.includes('/api/dynamic/')) {
    window.dispatchEvent(new Event('pmd-lookups-invalidated'));
  }
  return response;
});

export const adminUsersApi = {
  getAll: () => api.get('/api/auth/users')
};

export const authApi = {
  getMe: () => api.get('/api/auth/me')
};

// ===== Development APIs =====
export const devFilesApi = {
  // Task Log (SQL-backed via DevTrack)
  getTaskLogJson: () => api.get('/api/devtrack/tasks'),
  createTask: (task) => api.post('/api/devtrack/tasks', task),
  updateTask: (id, task) => api.put(`/api/devtrack/tasks/${id}`, task),
  deleteTask: (id) => api.delete(`/api/devtrack/tasks/${id}`),
  getTasksArchive: () => api.get('/api/devtrack/tasks/archive'),
  archiveCompletedTasks: () => api.post('/api/devtrack/tasks/archive'),
  // Dev Plan (SQL-backed via DevTrack)
  getDevPlan: () => api.get('/api/devtrack/plan'),
  updateDevPlanStep: (stepNumber, step) => api.put(`/api/devtrack/plan/step/${stepNumber}`, step),
  addDevPlanStep: (step) => api.post('/api/devtrack/plan', step),
  deleteDevPlanStep: (stepNumber) => api.delete(`/api/devtrack/plan/step/${stepNumber}`),
  moveDevPlanStep: (stepNumber, direction) => api.post(`/api/devtrack/plan/step/${stepNumber}/move`, { direction }),
  reassignDevPlanStep: (stepNumber, targetStep) => api.post(`/api/devtrack/plan/step/${stepNumber}/reassign`, { targetStep }),
  closeDevPlanStep: (stepNumber, summary, changes) => api.post(`/api/devtrack/plan/step/${stepNumber}/close`, { summary, changes }),
  reorderDevPlan: (items) => api.put('/api/devtrack/plan/reorder', items),
  // Rev Log (SQL-backed via DevTrack)
  getRevLog: () => api.get('/api/devtrack/revlog'),
  addRevision: (revision) => api.post('/api/devtrack/revlog', revision),
  updateRevision: (id, revision) => api.put(`/api/devtrack/revlog/${id}`, revision),
  deleteRevision: (id) => api.delete(`/api/devtrack/revlog/${id}`),
  // Lightweight version fetch — the only rev-log endpoint that's NOT
  // SysAdmin-gated (backed by [AllowAnonymous] on the controller). Used
  // by Footer + ReportViewer for the dynamic version string.
  getCurrentVersion: () => api.get('/api/devtrack/revlog/current-version'),
  // Schema & Dev Tools (still file-based)
  getSchema: () => api.get('/api/devfiles/schema'),
  getLiveSchema: () => api.get('/api/devfiles/schema/live'),
  getClaudeNotes: () => api.get('/api/devfiles/claude-notes'),
  updateClaudeNotes: (content) => api.put('/api/devfiles/claude-notes', { content }),
  // Module Configs (SQL-backed via AppCfg_DynamicModules)
  getModuleConfigs: () => api.get('/api/devfiles/module-configs'),
  // Moved to /api/module-config (a20.046) — runtime UI metadata, not a
  // dev-only endpoint. Backed by ModuleConfigController with [RequireDbAccess].
  // DevFilesController still owns PUT/DELETE/refresh-schema under [RequireSysAdmin].
  getModuleConfig: (tableName) => api.get(`/api/module-config/${tableName}`),
  saveModuleConfig: (tableName, data) => api.put(`/api/devfiles/module-config/${tableName}`, data),
  refreshModuleConfigSchema: (tableName) => api.post(`/api/devfiles/module-config/${tableName}/refresh-schema`),
  deleteModuleConfig: (tableName) => api.delete(`/api/devfiles/module-config/${tableName}`),
  // API status
  getTablesWithApi: () => api.get('/api/devfiles/tables-with-api'),
  // Table field names (lightweight — for FK target dropdowns)
  getTableFields: (tableName) => api.get(`/api/devfiles/table-fields/${tableName}`),
  // UI Audit — style guide computed styles + usage scanner
  getUiAuditStyles: (cssClass) => api.get(`/api/devfiles/ui-audit/styles`, { params: { class: cssClass } }),
  getUiAuditUsages: (cssClass) => api.get(`/api/devfiles/ui-audit/usages`, { params: { class: cssClass } })
};

// ===== Prod Deploy API =====
export const prodDeployApi = {
  getUndeployed:  () => api.get('/api/devtrack/prod-deploy/undeployed'),
  execute:        (dto) => api.post('/api/devtrack/prod-deploy/execute', dto),
  workflowStatus: () => api.get('/api/devtrack/prod-deploy/workflow-status'),
};

// ===== Dynamic API - Generic CRUD with auto FK resolution =====
export const dynamicApi = {
  getAll: (tableName, params = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.append('limit', params.limit);
    if (params.search) qs.append('search', params.search);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return api.get(`/api/dynamic/${tableName}${query}`);
  },
  getFiltered: (tableName, filterField, filterValue) =>
    api.get(`/api/dynamic/${tableName}?filterField=${filterField}&filterValue=${filterValue}`),
  getById: (tableName, id) => api.get(`/api/dynamic/${tableName}/${id}`),
  getLookups: (tableName, fieldName) => api.get(`/api/dynamic/${tableName}/lookups/${fieldName}`),
  create: (tableName, data, options = {}) => api.post(`/api/dynamic/${tableName}`, data, options),
  update: (tableName, id, data, options = {}) => api.put(`/api/dynamic/${tableName}/${id}`, data, options),
  delete: (tableName, id, options = {}) => api.delete(`/api/dynamic/${tableName}/${id}`, options),
  // Single-transaction bulk delete. Backend rolls back all deletes if any one
  // fails, so we never end up in a half-deleted state (was a real bug with the
  // previous Promise.all(deletes) implementation — deadlocked under contention
  // and left the user with partial state and no way to tell which failed).
  deleteMultiple: (tableName, ids, options = {}) =>
    api.post(`/api/dynamic/${tableName}/delete-bulk`, { ids }, options)
};

// ===== KPI API - Dashboard aggregations =====
export const kpiApi = {
  getHome: () => api.get('/api/kpi/home'),
  getDocumentation: () => api.get('/api/kpi/documentation'),
  getNetworking: () => api.get('/api/kpi/networking'),
  getEquipment: () => api.get('/api/kpi/equipment'),
  getSoo: () => api.get('/api/kpi/soo')
};

// ===== App Configuration API =====
export const appCfgApi = {
  // Display Settings
  getDisplaySettings: () => api.get('/api/appcfg/display-settings'),
  getDisplaySetting: (key) => api.get(`/api/appcfg/display-settings/${key}`),
  updateDisplaySetting: (id, data) => api.put(`/api/appcfg/display-settings/${id}`, data),

  // Field Metadata
  getAllFieldMetadata: () => api.get('/api/appcfg/fieldmetadata'),
  getTableList: () => api.get('/api/appcfg/fieldmetadata/tables'),
  getFieldMetadata: (tableName) => api.get(`/api/appcfg/fieldmetadata/${tableName}`),
  updateFieldMetadata: (id, data) => api.put(`/api/appcfg/fieldmetadata/${id}`, data),
  updateFieldMetadataBatch: (fields) => api.put('/api/appcfg/fieldmetadata/batch', fields),

  // Sync
  syncFieldMetadata: () => api.post('/api/appcfg/sync'),

  // Database metadata
  getDatabaseViews: () => api.get('/api/appcfg/views'),
  getViewColumns: (viewName) => api.get(`/api/appcfg/views/${encodeURIComponent(viewName)}/columns`),

  // Lookup Categories
  getAllLookupCategories: () => api.get('/api/appcfg/lookup-categories'),
  getLookupCategory: (category) => api.get(`/api/appcfg/lookup-categories/${category}`),
  createLookupCategory: (data) => api.post('/api/appcfg/lookup-categories', data),
  updateLookupCategory: (category, data) => api.put(`/api/appcfg/lookup-categories/${category}`, data),
  deleteLookupCategory: (category) => api.delete(`/api/appcfg/lookup-categories/${category}`),

  // Lookup Values
  getAllLookups: () => api.get('/api/appcfg/lookups'),
  getLookupsByCategory: (category) => api.get(`/api/appcfg/lookups/${category}`),
  getLookupCategories: () => api.get('/api/appcfg/lookups/categories'),
  createLookup: (data) => api.post('/api/appcfg/lookups', data),
  updateLookup: (id, data) => api.put(`/api/appcfg/lookups/${id}`, data),
  deleteLookup: (id) => api.delete(`/api/appcfg/lookups/${id}`)
};

// ===== Database Sync API =====
export const syncApi = {
  start: (request) => api.post('/api/sync/start', request),
  getStatus: () => api.get('/api/sync/status'),
  getHistory: (project) => api.get(`/api/sync/history/${project}`),
  getAllHistory: () => api.get('/api/sync/history'),
  getAvailability: () => api.get('/api/sync/availability'),
  getPlan: (project, phase) => api.get(`/api/sync/plan/${project}/${phase}`),
  syncViews: (target, environment) => api.post('/api/sync/sync-views', { target, environment }),
  getViewTargets: (environment) => api.get('/api/sync/view-targets', { params: { environment } }),
  getViewHistory: (target) => api.get(`/api/sync/view-history/${target}`)
};

// ===== Schema Sync API =====
export const schemaSyncApi = {
  start: (request) => api.post('/api/schema-sync/start', request),
  getStatus: () => api.get('/api/schema-sync/status'),
  getHistory: (project) => api.get(`/api/schema-sync/history/${project}`)
};

// ===== Admin Config Sync API (DevPlan step 86) =====
// Reconciles AppCfg_* config between the preprod and prod ADMIN databases.
// getDiff is read-only. apply requires an explicit row list AND the target database name
// typed back — there is deliberately no "sync everything" call.
export const adminConfigSyncApi = {
  getAvailability: () => api.get('/api/adminconfigsync/availability'),
  getDirections: () => api.get('/api/adminconfigsync/directions'),
  getDiff: (direction) => api.get('/api/adminconfigsync/diff', { params: { direction } }),
  apply: (direction, rows, confirmTargetDatabase) =>
    api.post('/api/adminconfigsync/apply', { direction, rows, confirmTargetDatabase }),
  getHistory: () => api.get('/api/adminconfigsync/history')
};

// ===== Admin Management API (Step 37 - RBAC) =====
export const adminManagementApi = {
  // Summary
  getSummary: () => api.get('/api/admin/summary'),
  // Table Ownership — governs DynamicController CRUD authorization (pen test R2)
  getTableOwners:   () => api.get('/api/admin/table-owner'),
  getTableOwner:    (tableName) => api.get(`/api/admin/table-owner/${tableName}`),
  createTableOwner: (data) => api.post('/api/admin/table-owner', data),
  updateTableOwner: (tableName, data) => api.put(`/api/admin/table-owner/${tableName}`, data),
  deleteTableOwner: (tableName) => api.delete(`/api/admin/table-owner/${tableName}`),
  // Organizations
  getOrganizations: () => api.get('/api/admin/organizations'),
  getOrganization: (id) => api.get(`/api/admin/organizations/${id}`),
  createOrganization: (data) => api.post('/api/admin/organizations', data),
  updateOrganization: (id, data) => api.put(`/api/admin/organizations/${id}`, data),
  deleteOrganization: (id) => api.delete(`/api/admin/organizations/${id}`),
  // Databases
  getDatabases: () => api.get('/api/admin/databases'),
  getDatabase: (id) => api.get(`/api/admin/databases/${id}`),
  createDatabase: (data) => api.post('/api/admin/databases', data),
  updateDatabase: (id, data) => api.put(`/api/admin/databases/${id}`, data),
  // Database provisioning (step 66)
  provisionDatabase: (data) => api.post('/api/admin/databases/provision', data),
  decommissionDatabase: (id) => api.post(`/api/admin/databases/${id}/decommission`),
  restoreDatabase: (id) => api.post(`/api/admin/databases/${id}/restore`),
  hardDeleteDatabase: (id, data) => api.delete(`/api/admin/databases/${id}`, { data }),
  getProvisioningRecord: (recordId) => api.get(`/api/admin/databases/provisioning/${recordId}`),
  getProvisioningHistory: (limit = 20) => api.get(`/api/admin/databases/provisioning?limit=${limit}`),
  // Permissions
  getPermissions: () => api.get('/api/admin/permissions'),
  getPermission: (id) => api.get(`/api/admin/permissions/${id}`),
  createPermission: (data) => api.post('/api/admin/permissions', data),
  updatePermission: (id, data) => api.put(`/api/admin/permissions/${id}`, data),
  deletePermission: (id) => api.delete(`/api/admin/permissions/${id}`),
  // Roles (DefRoles capability matrix)
  getDefRoles: () => api.get('/api/admin/roles'),
  getRoleMatrix: () => api.get('/api/admin/roles/matrix'),
  createDefRole: (data) => api.post('/api/admin/roles', data),
  updateDefRole: (id, data) => api.put(`/api/admin/roles/${id}`, data),
  updateRoleMatrix: (updates) => api.put('/api/admin/roles/matrix', updates),
  deleteDefRole: (id) => api.delete(`/api/admin/roles/${id}`),
  // User Roles
  getUserRoles: () => api.get('/api/admin/userroles'),
  getUserRolesForUser: (userId) => api.get(`/api/admin/userroles/user/${userId}`),
  createUserRole: (data) => api.post('/api/admin/userroles', data),
  updateUserRole: (id, data) => api.put(`/api/admin/userroles/${id}`, data),
  deleteUserRole: (id) => api.delete(`/api/admin/userroles/${id}`),
  // User Permissions (overrides)
  getUserPermissions: () => api.get('/api/admin/userpermissions'),
  getPermissionsForUserRole: (userRoleId) => api.get(`/api/admin/userpermissions/userrole/${userRoleId}`),
  createUserPermission: (data) => api.post('/api/admin/userpermissions', data),
  updateUserPermission: (id, data) => api.put(`/api/admin/userpermissions/${id}`, data),
  deleteUserPermission: (id) => api.delete(`/api/admin/userpermissions/${id}`),
  // Global Permissions
  getGlobalPermissions: (userId) => api.get(`/api/admin/users/${userId}/global-permissions`),
  addGlobalPermission: (userId, data) => api.post(`/api/admin/users/${userId}/global-permissions`, data),
  deleteGlobalPermission: (id) => api.delete(`/api/admin/global-permissions/${id}`),
  // Users (admin management view)
  getUsers: () => api.get('/api/admin/users'),
  getUser: (id) => api.get(`/api/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  copyUserRoles: (sourceId, targetId) => api.post(`/api/admin/users/${sourceId}/copy-to/${targetId}`),
  resetUserPermissions: (userId) => api.post(`/api/admin/users/${userId}/reset-permissions`),
  // System Health
  getSystemHealth: (period = '1h') => api.get('/api/admin/system/health', { params: { period } }),
  getDatabaseSizes: () => api.get('/api/admin/system/database-sizes'),
  // Permission Resolution
  resolvePermissions: (userId, dbId) => api.get(`/api/admin/resolve/${userId}/${dbId}`),
  resolveAllPermissions: (userId) => api.get(`/api/auth/resolve-all/${userId}`),
  // Export
  exportUsers: () => api.get('/api/admin/export/users', { responseType: 'blob' }),
  exportRoleMatrix: () => api.get('/api/admin/export/role-matrix', { responseType: 'blob' }),
  // Import
  importUserRoles: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/import/userroles', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Organization Images
  uploadOrgImage: (orgId, imageType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/admin/organizations/${orgId}/upload/${imageType}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteOrgImage: (orgId, imageType) => api.delete(`/api/admin/organizations/${orgId}/image/${imageType}`),
  // User creation & invitations
  createUser:        (data)   => api.post('/api/admin/users', data),
  getInvitation:     (userId) => api.get(`/api/admin/users/${userId}/invitation`),
  sendInvite:        (userId) => api.post(`/api/admin/users/${userId}/invite`),
  revokeInvite:      (userId) => api.delete(`/api/admin/users/${userId}/invite`),
  sendTestEmail:     (userId) => api.post(`/api/admin/users/${userId}/test-email`),
};

// ===== Invitations API (public — no auth) =====
export const invitationApi = {
  validate: (token)              => api.get(`/api/invitations/${token}`),
  accept:   (token, accessToken) => axios.post(
    `${API_BASE_URL}/api/invitations/${token}/accept`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } }
  ),
};

// ===== Data Entry Module API =====
export const dataEntryApi = {
  getModules:     ()               => api.get('/api/de-modules'),
  getModule:      (slug)           => api.get(`/api/de-modules/${slug}`),
  saveModule:     (slug, data)     => api.put(`/api/de-modules/${slug}`, data),
  deleteModule:   (slug)           => api.delete(`/api/de-modules/${slug}`),
  getTableSchema: (tableName)      => api.get(`/api/de-modules/schema/${tableName}`),
  getProjectTables: ()             => api.get('/api/de-modules/tables')
};

// ===== Database Selector API =====
export const databasesApi = {
  getAvailable: () => api.get('/api/databases/available')
};

// ===== User Preferences API =====
// userId: optional — self by default; SysAdmin may target another user
// (impersonation writes pass the target id explicitly; see PreferencesContext).
export const preferencesApi = {
  get:     (userId)            => api.get('/api/preferences', { params: userId != null ? { userId } : {} }),
  save:    (rows, userId)      => api.put('/api/preferences', rows, { params: userId != null ? { userId } : {} }),
  restore: (userId, dbId)      => api.delete('/api/preferences', {
    params: {
      ...(userId != null ? { userId } : {}),
      ...(dbId != null ? { databaseInstanceId: dbId } : {}),
    },
  }),
  // SysAdmin only — AppSec_PreferenceDefaults tier (Preference Defaults page)
  getDefaults:  ()     => api.get('/api/preferences/defaults'),
  saveDefaults: (rows) => api.put('/api/preferences/defaults', rows),
};

// ===== Reporting API =====
export const reportingApi = {
  // List reports visible to the caller. Post 2026-08-05 the response is a
  // filtered array of { slug, name, section } — filtered server-side by the
  // user's reports.<section> perms. Frontend uses this to build the nav.
  listConfigs: () => api.get('/api/report/configs'),
  getConfig: (reportId) => api.get(`/api/report/${reportId}/config`),
  runReport: (reportId, params) => api.post(`/api/report/${reportId}/run`, params),
  getParameterOptions: (reportId, parameterField, filters) => {
    const params = {};
    if (filters) params.filters = JSON.stringify(filters);
    return api.get(`/api/report/${reportId}/parameter-options/${parameterField}`, { params });
  }
};

// ===== Combined Tools API =====
export const combinedToolsApi = {
  getParents:   (slug)           => api.get(`/api/combined-tools/${slug}/parents`),
  getTags:      (slug, parent)   => api.get(`/api/combined-tools/${slug}/tags`, { params: { parent } }),
  getPanels:    (slug, parent)   => api.get(`/api/combined-tools/${slug}/panels`, { params: { parent } }),
  getPlatforms: (slug)           => api.get(`/api/combined-tools/${slug}/platforms`),
  generate:     (slug, body)     => api.post(`/api/combined-tools/${slug}/generate`, body),
};

// ===== User Activity API (cross-session transfer tracking) =====
export const userActivityApi = {
  getActive:  (userId)              => api.get('/api/useractivity',          { params: { userId } }),
  getHistory: (userId, range, q)    => api.get('/api/useractivity/history',  { params: { userId, range, q } }),
  register:   (dto)                 => api.post('/api/useractivity',          dto),
  update:     (activityId, userId, dto) => api.patch(`/api/useractivity/${activityId}`, dto, { params: { userId } }),
  delete:     (activityId, userId)  => api.delete(`/api/useractivity/${activityId}`, { params: { userId } }),
};

export const integrationConfigApi = {
  // Slugs
  getSlugs: () => api.get('/api/integration-config/slugs'),

  // Platforms
  getPlatforms:    (slug)          => api.get(`/api/integration-config/${slug}/platforms`),
  createPlatform:  (slug, body)    => api.post(`/api/integration-config/${slug}/platforms`, body),
  updatePlatform:  (slug, id, body)=> api.put(`/api/integration-config/${slug}/platforms/${id}`, body),
  deletePlatform:  (slug, id)      => api.delete(`/api/integration-config/${slug}/platforms/${id}`),

  // Register Templates
  getTemplates:   (slug, platformId)          => api.get(`/api/integration-config/${slug}/platforms/${platformId}/templates`),
  addTemplate:    (slug, platformId, body)    => api.post(`/api/integration-config/${slug}/platforms/${platformId}/templates`, body),
  updateTemplate: (slug, platformId, id, body)=> api.put(`/api/integration-config/${slug}/platforms/${platformId}/templates/${id}`, body),
  deleteTemplate: (slug, platformId, id)      => api.delete(`/api/integration-config/${slug}/platforms/${platformId}/templates/${id}`),

  // Tag XRef
  getTagXRef:    (slug)          => api.get(`/api/integration-config/${slug}/tagxref`),
  addTagXRef:    (slug, body)    => api.post(`/api/integration-config/${slug}/tagxref`, body),
  updateTagXRef: (slug, id, body)=> api.put(`/api/integration-config/${slug}/tagxref/${id}`, body),
  deleteTagXRef: (slug, id)      => api.delete(`/api/integration-config/${slug}/tagxref/${id}`),

  // Copy-from
  getPlatformsFrom: (slug, sourceSlug)      => api.get(`/api/integration-config/${slug}/platforms/from/${sourceSlug}`),
  copyPlatforms:    (slug, body)            => api.post(`/api/integration-config/${slug}/platforms/copy`, body),
  getTagXRefFrom:   (slug, sourceSlug)      => api.get(`/api/integration-config/${slug}/tagxref/from/${sourceSlug}`),
  copyTagXRef:      (slug, body)            => api.post(`/api/integration-config/${slug}/tagxref/copy`, body),
};

export const udtCommentApi = {
  getEngUnits: (slug)           => api.get(`/api/udt-comment/${slug}/eng-units`),
  analyze:     (slug, csvContent) => api.post(`/api/udt-comment/${slug}/analyze`, csvContent, { headers: { 'Content-Type': 'application/json' } }),
  generate:    (slug, body)       => api.post(`/api/udt-comment/${slug}/generate`, body),
};

export const scadaTagApi = {
  getVersions: (system)      => api.get(`/api/scada-tag/versions?system=${system}`),
  generate:    (slug, formData) => api.post(`/api/scada-tag/${slug}/generate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const simulationTagApi = {
  getVersions:   (system)        => api.get(`/api/simulation-tag/versions?system=${system}`),
  getParents:    (slug)          => api.get(`/api/combined-tools/${slug}/parents`),
  getCandidates: (formData)      => api.post(`/api/simulation-tag/candidates`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  generate:      (slug, formData) => api.post(`/api/simulation-tag/${slug}/generate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const auditApi = {
  getRecordHistory: (tableName, recordId) =>
    api.get(`/api/audit/changes/${tableName}/${recordId}`),
  getAllChanges: (params) =>
    api.get('/api/audit/changes', { params }),
  purge: (olderThanDays) =>
    api.delete('/api/audit/purge', { params: { olderThanDays } }),
};

export const utilitiesApi = {
  getIoCounts: (spareThreshold = 0.20) =>
    api.get('/api/utilities/io-counts', { params: { spareThreshold } }),
  getDistanceApprox: (target) =>
    api.get('/api/utilities/distance-approx', { params: { target } }),
  getEstimateExportFilters: () =>
    api.get('/api/utilities/estimate-export/filters'),
  generateEstimateExport: (body) =>
    api.post('/api/utilities/estimate-export/rows', body),
};

export default api;
